import { WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { errorCodeOf, messageFor } from '@/api/errors';
import { useMe } from '@/api/queries/auth';
import { FullPageMessage } from '@/components/common/full-page-message';
import { FullPageSpinner } from '@/components/common/spinner';
import { Button } from '@/components/ui/button';
import { SessionContext } from './use-session';

/** Protects logged-in pages. Not logged in -> /login?next=<where you wanted to go>. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <FullPageSpinner />;
  if (me.isError) {
    const code = errorCodeOf(me.error);
    if (code === 'TENANT_SUSPENDED') return <Navigate to="/suspended" replace />;
    if (code === 'USER_DISABLED') return <Navigate to="/account-disabled" replace />;
    if (code === 'UNAUTHENTICATED') {
      const next = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?next=${next}`} replace />;
    }
    return (
      <FullPageMessage
        icon={WifiOff}
        title="We could not load your account"
        actions={<Button onClick={() => void me.refetch()}>Try again</Button>}
      >
        {messageFor(me.error)}
      </FullPageMessage>
    );
  }
  if (me.data.authType !== 'session') return <Navigate to="/login" replace />;
  return <SessionContext.Provider value={me.data}>{children}</SessionContext.Provider>;
}
